import { creerClientApi } from '@admin-pmvb/api/client'
import { type QueryClient, queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'

export const api = creerClientApi('/', { init: { credentials: 'same-origin' } }).api

export class NonConnecte extends Error {}

/** Erreur renvoyée par l'API : message général et, pour une saisie invalide, erreurs par champ. */
export class ErreurApi extends Error {
  /** Le message seul, sans le motif des champs : un formulaire les montre sous chaque champ. */
  readonly titre: string
  constructor(
    message: string,
    readonly statut: number,
    readonly champs: { champ: string; message: string }[] = [],
  ) {
    // « Saisie invalide. » ne dit pas quoi corriger : le motif de chaque champ suit.
    super([message, ...champs.map((c) => c.message)].join(' '))
    this.titre = message
  }
}

type Reponse = { ok: boolean; status: number; json(): Promise<unknown> }
/** Corps de la réponse réussie : les corps d'erreur ({ erreur }) sont exclus du type. */
type CorpsReussi<R> = R extends { json(): Promise<infer T> }
  ? Exclude<T, { erreur: string }>
  : never

/** Lit une réponse : le corps si elle est réussie, sinon une ErreurApi. */
export async function lire<R extends Reponse>(reponse: Promise<R>): Promise<CorpsReussi<R>> {
  const r = await reponse
  if (r.status === 401) throw new NonConnecte()
  const donnees = (await r.json()) as unknown
  if (!r.ok) {
    const e = donnees as { erreur?: string; champs?: { champ: string; message: string }[] }
    throw new ErreurApi(e.erreur ?? `Erreur ${r.status}`, r.status, e.champs)
  }
  return donnees as CorpsReussi<R>
}

export const requeteMoi = queryOptions({
  queryKey: ['auth', 'moi'],
  queryFn: () => lire(api.auth.moi.$get()),
  retry: false,
  staleTime: 60_000,
})

export const requeteConfigAuth = queryOptions({
  queryKey: ['auth', 'config'],
  queryFn: async () => (await api.auth.config.$get()).json(),
  staleTime: Infinity,
})

// Référentiel

export const requetes = {
  entites: queryOptions({ queryKey: ['entites'], queryFn: () => lire(api.entites.$get()) }),
  entite: (id: string) =>
    queryOptions({
      queryKey: ['entites', id],
      queryFn: () => lire(api.entites[':id'].$get({ param: { id } })),
    }),
  /** Plan de classement de l'entité : ses dossiers dans l'ordre de l'arbre. */
  plan: (id: string) =>
    queryOptions({
      queryKey: ['plan', id],
      queryFn: () => lire(api.entites[':id'].plan.$get({ param: { id } })),
    }),
  personnes: queryOptions({ queryKey: ['personnes'], queryFn: () => lire(api.personnes.$get()) }),
  roles: (entiteId?: string) =>
    queryOptions({
      queryKey: ['roles', entiteId ?? 'tous'],
      queryFn: () => lire(api.roles.$get({ query: entiteId ? { entiteId } : {} })),
    }),
  tiers: queryOptions({ queryKey: ['tiers'], queryFn: () => lire(api.tiers.$get({ query: {} })) }),
  biens: (entiteId: string) =>
    queryOptions({
      queryKey: ['biens', entiteId],
      queryFn: () => lire(api.biens.$get({ query: { entiteId } })),
    }),
  baux: (entiteId: string) =>
    queryOptions({
      queryKey: ['baux', entiteId],
      queryFn: () => lire(api.baux.$get({ query: { entiteId } })),
    }),
  contrats: (entiteId: string) =>
    queryOptions({
      queryKey: ['contrats', entiteId],
      queryFn: () => lire(api.contrats.$get({ query: { entiteId } })),
    }),
  abonnements: (entiteId?: string) =>
    queryOptions({
      queryKey: ['abonnements', entiteId ?? 'toutes'],
      queryFn: () => lire(api.abonnements.$get({ query: entiteId ? { entiteId } : {} })),
    }),
  comptes: (entiteId: string) =>
    queryOptions({
      queryKey: ['comptes', entiteId],
      queryFn: () => lire(api.comptes.$get({ query: { entiteId } })),
    }),
  echeances: (filtre: { du?: string; au?: string; entiteId?: string } = {}) =>
    queryOptions({
      queryKey: ['echeances', filtre],
      queryFn: () => lire(api.echeances.$get({ query: filtre })),
    }),
  echeance: (id: string) =>
    queryOptions({
      queryKey: ['echeances', 'fiche', id],
      queryFn: () => lire(api.echeances[':id'].$get({ param: { id } })),
    }),
  regles: queryOptions({
    queryKey: ['regles'],
    queryFn: () => lire(api.regles.$get({ query: {} })),
  }),
  paiements: (filtre: { vue?: VuePaiements; entiteId?: string; echeanceId?: string } = {}) =>
    queryOptions({
      queryKey: ['paiements', filtre],
      queryFn: () => lire(api.paiements.$get({ query: filtre })),
    }),
  paiement: (id: string) =>
    queryOptions({
      queryKey: ['paiements', 'fiche', id],
      queryFn: () => lire(api.paiements[':id'].$get({ param: { id } })),
    }),
  imports: queryOptions({
    queryKey: ['releves'],
    queryFn: () => lire(api.releves.$get({ query: {} })),
  }),
  transactionsNonAffectees: queryOptions({
    queryKey: ['transactions', 'non-affectees'],
    queryFn: () => lire(api.transactions.$get({ query: { nonAffectees: '1' } })),
  }),
  decisions: queryOptions({
    queryKey: ['rapprochements', 'decisions'],
    queryFn: () => lire(api.rapprochements.decisions.$get()),
  }),
  propositions: (transactionId: string) =>
    queryOptions({
      queryKey: ['transactions', transactionId, 'propositions'],
      queryFn: () =>
        lire(api.transactions[':id'].propositions.$get({ param: { id: transactionId } })),
    }),
  delaisGrace: queryOptions({
    queryKey: ['reglages', 'delais-grace'],
    queryFn: () => lire(api.reglages['delais-grace'].$get()),
  }),
  taches: (
    filtre: {
      vue?: VueTaches
      entiteId?: string
      echeanceId?: string
      paiementId?: string
      projetId?: string
      /** Mots cherchés dans le titre ou les précisions. */
      q?: string
      /** Pour qui : une personne ou « tous » ; absent, le défaut de la vue. */
      pour?: string
    } = {},
  ) =>
    queryOptions({
      queryKey: ['taches', filtre],
      queryFn: () => lire(api.taches.$get({ query: filtre })),
    }),
  /** Tâches nouvelles de la personne connectée : la pastille, relue toutes les 30 s. */
  nouvellesTaches: queryOptions({
    queryKey: ['taches', 'nouvelles'],
    queryFn: () => lire(api.taches.nouvelles.$get()),
    refetchInterval: 30_000,
  }),
  tache: (id: string) =>
    queryOptions({
      queryKey: ['taches', 'fiche', id],
      queryFn: () => lire(api.taches[':id'].$get({ param: { id } })),
    }),
  /** Projets où ranger la tâche : visibles, écrits, dans son espace. */
  projetsPourTache: (id: string) =>
    queryOptions({
      queryKey: ['taches', 'projets', id],
      queryFn: () => lire(api.taches[':id'].projets.$get({ param: { id } })),
    }),
  projets: (filtre: { statut?: StatutProjet } = {}) =>
    queryOptions({
      queryKey: ['projets', filtre],
      queryFn: () => lire(api.projets.$get({ query: filtre })),
    }),
  projet: (id: string) =>
    queryOptions({
      queryKey: ['projets', 'fiche', id],
      queryFn: () => lire(api.projets[':id'].$get({ param: { id } })),
    }),
  /** Guide de déclaration de la tâche (repères posés, progression) et guides de l'entité. */
  guideTache: (id: string) =>
    queryOptions({
      queryKey: ['taches', 'guide', id],
      queryFn: () => lire(api.taches[':id'].guide.$get({ param: { id } })),
    }),
  apercuDigest: queryOptions({
    queryKey: ['digest', 'apercu'],
    queryFn: () => lire(api.digest.apercu.$get()),
  }),
  pieces: (filtre: { vue?: VuePieces; q?: string; entiteId?: string; echeanceId?: string } = {}) =>
    queryOptions({
      queryKey: ['pieces', filtre],
      queryFn: () => lire(api.pieces.$get({ query: filtre })),
    }),
  /** Recherche partout : tâches, pièces, opérations, paiements, échéances, tiers. */
  recherche: (q: string) =>
    queryOptions({
      queryKey: ['recherche', q],
      queryFn: () => lire(api.recherche.$get({ query: { q } })),
      staleTime: 10_000,
    }),
  acheminement: queryOptions({
    queryKey: ['pieces', 'acheminement'],
    queryFn: () => lire(api.pieces.acheminement.$get()),
  }),
  /** Mail lu à la demande dans Gmail (jamais gardé) : pas de nouvel essai sur un refus. */
  mail: (id: string) =>
    queryOptions({
      queryKey: ['mails', id],
      queryFn: () => lire(api.mails[':id'].$get({ param: { id } })),
      retry: false,
      staleTime: 5 * 60_000,
    }),
  piece: (id: string) =>
    queryOptions({
      queryKey: ['pieces', 'fiche', id],
      queryFn: () => lire(api.pieces[':id'].$get({ param: { id } })),
    }),
  /** Débits trouvés sur les relevés pour une facture de boîte personnelle (décision du 13/10/2026). */
  debitsPiece: (id: string) =>
    queryOptions({
      queryKey: ['pieces', id, 'debits'],
      queryFn: () => lire(api.pieces[':id'].debits.$get({ param: { id } })),
    }),
  /** Liens Drive des dossiers du fil d'Ariane d'une pièce (relus dans le Drive à la demande). */
  filDrive: (id: string) =>
    queryOptions({
      queryKey: ['pieces', id, 'fil-drive'],
      queryFn: () => lire(api.pieces[':id']['fil-drive'].$get({ param: { id } })),
    }),
  supervision: queryOptions({
    queryKey: ['reglages', 'supervision'],
    queryFn: () => lire(api.reglages.supervision.$get()),
  }),
  /** Page « Circuits » : relue chaque minute tant qu'elle est ouverte (audit du jour). */
  circuits: (jours: '1' | '7' | '30') =>
    queryOptions({
      queryKey: ['reglages', 'circuits', jours],
      queryFn: () => lire(api.reglages.circuits.$get({ query: { jours } })),
      refetchInterval: 60_000,
    }),
  reglagesPieces: queryOptions({
    queryKey: ['reglages', 'pieces'],
    queryFn: () => lire(api.reglages.pieces.$get()),
  }),
  boites: queryOptions({ queryKey: ['boites'], queryFn: () => lire(api.boites.$get()) }),
  banques: queryOptions({ queryKey: ['banques'], queryFn: () => lire(api.banques.$get()) }),
  portails: queryOptions({ queryKey: ['portails'], queryFn: () => lire(api.portails.$get()) }),
  /** Collecteurs, ceux à lancer d'abord, avec leurs raisons (page « Collecteurs »). */
  collecteurs: queryOptions({
    queryKey: ['portails', 'collecteurs'],
    queryFn: () => lire(api.portails.collecteurs.$get()),
  }),
  /** Fournisseurs et voie d'arrivée de leurs pièces sur un an (Connexions, « Fournisseurs »). */
  voiesFournisseurs: queryOptions({
    queryKey: ['portails', 'fournisseurs'],
    queryFn: () => lire(api.portails.fournisseurs.$get()),
  }),
  /** Ordinateurs de la personne connectée (collecteur local). */
  ordinateurs: queryOptions({
    queryKey: ['ordinateurs'],
    queryFn: () => lire(api.ordinateurs.$get()),
  }),
  /** Défis qui attendent la personne connectée (titulaire) : la surimpression. */
  mesDefis: queryOptions({
    queryKey: ['portails', 'mes-defis'],
    queryFn: () => lire(api.portails.defis.$get()),
  }),
  /** Demande de disponibilité de la personne connectée (avant une relance) : la surimpression. */
  maDisponibilite: queryOptions({
    queryKey: ['portails', 'disponibilite'],
    queryFn: () => lire(api.portails.disponibilite.$get()),
  }),
  defi: (id: string) =>
    queryOptions({
      queryKey: ['portails', 'defi', id],
      queryFn: () => lire(api.portails.defis[':id'].$get({ param: { id } })),
    }),
  notifications: queryOptions({
    queryKey: ['notifications'],
    queryFn: () => lire(api.notifications.$get()),
  }),
  banquesDisponibles: queryOptions({
    queryKey: ['banques', 'disponibles'],
    queryFn: () => lire(api.banques.disponibles.$get({ query: { pays: 'FR' } })),
    staleTime: 3_600_000,
  }),
  lienIcal: queryOptions({ queryKey: ['auth', 'ical'], queryFn: () => lire(api.auth.ical.$get()) }),
  tousLesBaux: queryOptions({
    queryKey: ['baux', 'tous'],
    queryFn: () => lire(api.baux.$get({ query: {} })),
  }),
  consignes: queryOptions({
    queryKey: ['consignes'],
    queryFn: () => lire(api.consignes.$get({ query: {} })),
  }),
  /** Toutes les consignes de fournisseur visibles, tous tiers confondus. */
  consignesFournisseur: queryOptions({
    queryKey: ['consignes-fournisseur', 'toutes'],
    queryFn: () => lire(api['consignes-fournisseur'].$get({ query: {} })),
  }),
  brouillons: (filtre: { vue?: VueBrouillons; tacheId?: string; projetId?: string } = {}) =>
    queryOptions({
      queryKey: ['brouillons', filtre],
      queryFn: () => lire(api.brouillons.$get({ query: filtre })),
    }),
  brouillon: (id: string) =>
    queryOptions({
      queryKey: ['brouillons', 'fiche', id],
      queryFn: () => lire(api.brouillons[':id'].$get({ param: { id } })),
    }),
  membres: queryOptions({
    queryKey: ['sante', 'membres'],
    queryFn: () => lire(api.beneficiaires.$get({ query: {} })),
  }),
  soins: (filtre: { beneficiaireId?: string; annee?: string; ouverts?: 'true' } = {}) =>
    queryOptions({
      queryKey: ['sante', 'soins', filtre],
      queryFn: () => lire(api.soins.$get({ query: filtre })),
    }),
  soin: (id: string) =>
    queryOptions({
      queryKey: ['sante', 'soin', id],
      queryFn: () => lire(api.soins[':id'].$get({ param: { id } })),
    }),
  /** Frais à refacturer : lignes (état calculé) d'une entité ou d'une pièce. */
  refacturations: (filtre: { entiteId?: string; pieceId?: string } = {}) =>
    queryOptions({
      queryKey: ['refacturations', 'lignes', filtre],
      queryFn: () => lire(api.refacturations.$get({ query: filtre })),
    }),
  lotsRefacturation: (entiteId?: string) =>
    queryOptions({
      queryKey: ['refacturations', 'lots', entiteId ?? 'toutes'],
      queryFn: () => lire(api['lots-refacturation'].$get({ query: entiteId ? { entiteId } : {} })),
    }),
  /** Tableau des cartes : l'entité qui paie avec chacune. */
  cartes: queryOptions({ queryKey: ['cartes'], queryFn: () => lire(api.cartes.$get()) }),
  journal: (filtre: { entiteId?: string; objetId?: string }) =>
    queryOptions({
      queryKey: ['journal', filtre],
      queryFn: () => lire(api.journal.$get({ query: filtre })),
    }),
}

export type Entite = InferResponseType<typeof api.entites.$get, 200>[number]
export type Personne = InferResponseType<typeof api.personnes.$get, 200>[number]
export type Role = InferResponseType<typeof api.roles.$get, 200>[number]
export type Tiers = InferResponseType<typeof api.tiers.$get, 200>[number]
export type Bien = InferResponseType<typeof api.biens.$get, 200>[number]
export type Bail = InferResponseType<typeof api.baux.$get, 200>[number]
export type Contrat = InferResponseType<typeof api.contrats.$get, 200>[number]
export type Abonnement = InferResponseType<typeof api.abonnements.$get, 200>[number]
export type Compte = InferResponseType<typeof api.comptes.$get, 200>[number]
export type Plan = InferResponseType<(typeof api.entites)[':id']['plan']['$get'], 200>
export type Dossier = Plan['dossiers'][number]
export type Circuits = InferResponseType<typeof api.reglages.circuits.$get, 200>
export type Echeance = InferResponseType<typeof api.echeances.$get, 200>[number]
export type FicheEcheance = InferResponseType<(typeof api.echeances)[':id']['$get'], 200>
export type Regle = InferResponseType<typeof api.regles.$get, 200>[number]
export type VuePaiements =
  'a_payer' | 'a_encaisser' | 'en_retard' | 'partiels' | 'a_rapprocher' | 'releves_manquants'
export type Paiement = InferResponseType<typeof api.paiements.$get, 200>[number]
export type Decision = InferResponseType<typeof api.rapprochements.decisions.$get, 200>[number]
export type VueTaches = 'ma_semaine' | 'tableau' | 'en_attente_tiers' | 'reportees' | 'toutes'
export type Tache = InferResponseType<typeof api.taches.$get, 200>[number]
export type FicheTache = InferResponseType<(typeof api.taches)[':id']['$get'], 200>
export type StatutProjet = 'en_cours' | 'termine' | 'abandonne'
export type ProjetListe = InferResponseType<typeof api.projets.$get, 200>[number]
export type FicheProjet = InferResponseType<(typeof api.projets)[':id']['$get'], 200>
export type FilProjet = FicheProjet['fils'][number]
/** Consigne de tri proposée par Claude depuis une tâche, non écrite. */
export type PropositionConsigne = InferResponseType<
  (typeof api.taches)[':id']['proposer-consigne']['$post'],
  200
>
export type VuePieces = 'recentes' | 'non_rattachees' | 'a_suivre' | 'toutes'
export type Piece = InferResponseType<typeof api.pieces.$get, 200>[number]
export type FichePiece = InferResponseType<(typeof api.pieces)[':id']['$get'], 200>
export type DebitsPiece = InferResponseType<(typeof api.pieces)[':id']['debits']['$get'], 200>
export type LigneJournal = InferResponseType<typeof api.journal.$get, 200>[number]
export type Consigne = InferResponseType<typeof api.consignes.$get, 200>[number]
export type ConsigneFournisseur = InferResponseType<
  (typeof api)['consignes-fournisseur']['$get'],
  200
>[number]
export type VueBrouillons = 'a_traiter' | 'faits' | 'tous'
export type Brouillon = InferResponseType<typeof api.brouillons.$get, 200>[number]
export type Membre = InferResponseType<typeof api.beneficiaires.$get, 200>[number]
export type Soin = InferResponseType<typeof api.soins.$get, 200>[number]
export type FicheSoin = InferResponseType<(typeof api.soins)[':id']['$get'], 200>
export type FicheBrouillon = InferResponseType<(typeof api.brouillons)[':id']['$get'], 200>
export type LigneRefacturation = InferResponseType<typeof api.refacturations.$get, 200>[number]
export type LotRefacturation = InferResponseType<
  (typeof api)['lots-refacturation']['$get'],
  200
>[number]

/**
 * Valide une saisie avec le schéma partagé (packages/core) avant l'envoi :
 * mêmes règles et mêmes messages que l'API, erreurs rattachées aux champs.
 */
export function corps<S extends { safeParse(v: unknown): SafeParse<unknown> }>(
  schema: S,
  valeurs: unknown,
): S extends { safeParse(v: unknown): SafeParse<infer T> } ? T : never {
  const r = schema.safeParse(valeurs)
  if (!r.success) {
    throw new ErreurApi(
      'Saisie invalide.',
      400,
      r.error.issues.map((i) => ({ champ: i.path.join('.'), message: i.message })),
    )
  }
  return r.data as never
}

type SafeParse<T> =
  | { success: true; data: T }
  | { success: false; error: { issues: { path: PropertyKey[]; message: string }[] } }

/** La personne connectée gère-t-elle cette entité (fiche, archivage, personnes, rôles) ? */
export function gere(moi: { gestion: readonly string[] }, entiteId: string): boolean {
  return moi.gestion.includes(entiteId)
}

/**
 * La personne connectée écrit-elle sur cette entité ? Liste explicite du serveur, sans
 * raccourci administrateur : un espace privé lu par un accès temporaire (lecture seule) n'y
 * figure pas, même pour un administrateur. Ne dit rien de l'archivage (à tester à part).
 */
export function ecrit(moi: { ecriture: readonly string[] }, entiteId: string): boolean {
  return moi.ecriture.includes(entiteId)
}

/**
 * /moi est-il plus ancien que ces entités lues ? Une entité qu'il ne range ni en écriture ni
 * parmi celles lues par un accès temporaire a été créée (ou ouverte) après lui : par l'autre
 * administrateur, par exemple. Ses listes de choix d'écriture l'oublieraient.
 */
export function perimetreEnRetard(
  moi: { ecriture: readonly string[]; parAcces: readonly string[] },
  entites: readonly string[],
): boolean {
  return entites.some((id) => !moi.ecriture.includes(id) && !moi.parAcces.includes(id))
}

/** Identifiants des entités d'une réponse en cache (liste ou fiche), sinon rien. */
function entitesLues(cle: readonly unknown[], donnees: unknown): string[] {
  if (cle[0] !== 'entites' || cle.length > 2) return []
  const lignes = cle.length === 1 ? donnees : [donnees]
  if (!Array.isArray(lignes)) return []
  return lignes.flatMap((l: unknown) =>
    l && typeof l === 'object' && 'id' in l && typeof l.id === 'string' ? [l.id] : [],
  )
}

/**
 * Relit /moi dès qu'une liste ou une fiche d'entité arrive avec une entité qu'il ne connaît pas
 * (`perimetreEnRetard`), au lieu d'attendre la fin de son minuteur : toutes les pages qui
 * proposent une écriture la voient alors. Renvoie de quoi arrêter de suivre.
 */
export function suivreLePerimetre(client: QueryClient): () => void {
  return client.getQueryCache().subscribe((e) => {
    if (e.type !== 'updated' || e.action.type !== 'success') return
    const ids = entitesLues(e.query.queryKey, e.action.data)
    if (ids.length === 0) return
    const moi = client.getQueryData<{ ecriture: string[]; parAcces: string[] }>(requeteMoi.queryKey)
    if (moi && perimetreEnRetard(moi, ids))
      void client.invalidateQueries({ queryKey: requeteMoi.queryKey })
  })
}

// Envois photo

export const requeteEnvois = queryOptions({
  queryKey: ['envois'],
  queryFn: () => lire(api.envois.$get()),
})

/** Fichier en base64, par tranches (évite de dépasser la pile sur un gros fichier). */
export function enBase64(octets: ArrayBuffer): string {
  const tableau = new Uint8Array(octets)
  let binaire = ''
  for (let i = 0; i < tableau.length; i += 0x8000)
    binaire += String.fromCharCode(...tableau.subarray(i, i + 0x8000))
  return btoa(binaire)
}

export type GuideDeTache = InferResponseType<(typeof api.taches)[':id']['guide']['$get'], 200>
